import type { TFunction } from 'i18next';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import {
  AdvisorSituationKey,
  OptimizerDecisionImpact,
  OptimizerDecisionStage,
} from './advisorSituationKeys';
import { buildTaxLocationAccountFlows } from './buildTaxLocationAccountFlows';
import {
  buildSubAccountNamesById,
  formatAdvisorDecisionAction,
  resolveAdvisorSituationKey,
} from './formatAdvisorInsightText';
import { sanitizeAdvisorSummaryForAi } from './formatAdvisorSummaryForAi';
import {
  FUNDING_SEGREGATION_APPLIED_PATTERN,
  parseStructuralFundingDriftShares,
  STRUCTURAL_FUNDING_DRIFT_PATTERN,
} from './optimizerDecisionActionPatterns';

const ENGINE_CENT_PATTERN = /\b\d+c\b/gi;
const FLOW_LABEL_CAP = 2;

export type RunSituationExampleOptions = {
  portfolioLabel?: string;
};

/** Qualitative exemplars for the model — no dollars or engine cent literals. */
export const sanitizeRunSituationExampleForAi = (summary: string): string =>
  sanitizeAdvisorSummaryForAi(summary.replace(ENGINE_CENT_PATTERN, ''))
    .replace(/\bRaised\s+/i, 'Cash raised ')
    .replace(/\bDeployed\s+/i, 'Cash deployed ')
    .replace(/\s+,/g, ',')
    .replace(/,\s*,/g, ',')
    .replace(/\s{2,}/g, ' ')
    .trim();

const prefixPortfolioLabel = (
  summary: string,
  portfolioLabel?: string,
): string => (portfolioLabel ? `${portfolioLabel} — ${summary}` : summary);

const formatMultiCurrencyStructuralDriftForAi = (
  action: string,
): string | undefined => {
  const shares = parseStructuralFundingDriftShares(action);
  if (!shares) {
    return undefined;
  }

  const { cadModelPct, cadFundablePct, usdModelPct, usdFundablePct } = shares;
  const cadGap = Number(cadFundablePct) - Number(cadModelPct);
  const usdGap = Number(usdFundablePct) - Number(usdModelPct);
  const cadShape =
    cadGap < 0
      ? `CAD pool underfunded vs model (${cadFundablePct}% fundable vs ${cadModelPct}% target)`
      : `CAD pool over model (${cadFundablePct}% vs ${cadModelPct}%)`;
  const usdShape =
    usdGap < 0
      ? `USD pool underfunded vs model (${usdFundablePct}% fundable vs ${usdModelPct}% target)`
      : `USD pool over model (${usdFundablePct}% vs ${usdModelPct}%)`;

  return `Multi-currency book — ${cadShape}; ${usdShape}`;
};

const formatMultiCurrencyExampleForAi = (
  action: string,
  t: TFunction,
): string | undefined => {
  const structuralDrift = formatMultiCurrencyStructuralDriftForAi(action);
  if (structuralDrift) {
    return structuralDrift;
  }

  if (STRUCTURAL_FUNDING_DRIFT_PATTERN.test(action)) {
    return t('bulkRebalance:insights.multiCurrencyNeedsConversion');
  }

  if (!FUNDING_SEGREGATION_APPLIED_PATTERN.test(action)) {
    return undefined;
  }

  const accountMatch = action.match(/sub-account\s+([^:]+):/i);
  const accountLabel = accountMatch?.[1]?.trim();
  const bookPrefix =
    accountLabel && !/^[a-f0-9]{24}$/i.test(accountLabel)
      ? ` (${accountLabel})`
      : '';

  const currencies = [...action.matchAll(/\b([A-Z]{3})\s+\d+(?:\.\d+)?%/g)].map(
    (match) => match[1],
  );

  if (currencies.length >= 2) {
    const currencyLabel =
      currencies.length === 2
        ? `${currencies[0]} and ${currencies[1]}`
        : `${currencies.slice(0, -1).join(', ')}, and ${currencies.at(-1)}`;

    return `Multi-currency book${bookPrefix} — ${currencyLabel} each fund their own holdings`;
  }

  return t('bulkRebalance:insights.multiCurrencyCashStays');
};

const formatTaxLocationExampleForAi = (
  audit: OptimizerAuditV2Record,
  baseSummary: string,
): string => {
  const flows = buildTaxLocationAccountFlows(
    audit,
    buildSubAccountNamesById(new Map(), audit),
    FLOW_LABEL_CAP,
  );

  if (flows.length === 0) {
    return baseSummary;
  }

  const flowLabels = flows.map(
    (flow) => `${flow.fromAccount} → ${flow.toAccount}`,
  );

  return `${baseSummary}; cross-sleeve flows include ${flowLabels.join(', ')}`;
};

/** Prefer exemplars with concrete sleeve/currency detail over generic stage labels. */
export const scoreRunSituationExampleSummary = (summary: string): number => {
  let score = summary.length;

  if (summary.includes('→')) {
    score += 40;
  }

  if (/CAD|USD|multi-currency book/i.test(summary)) {
    score += 30;
  }

  if (/cross-sleeve flows include/i.test(summary)) {
    score += 35;
  }

  if (/underfunded vs model|over model/i.test(summary)) {
    score += 45;
  }

  if (/— [A-Z][^—]+ — /i.test(summary)) {
    score += 20;
  }

  return score;
};

export const formatRunSituationExampleSummaryForAi = (
  stage: string,
  audit: OptimizerAuditV2Record,
  t: TFunction,
  options: RunSituationExampleOptions = {},
): string | undefined => {
  const decision = (audit.decisions ?? []).find(
    (item) => resolveAdvisorSituationKey(item) === stage,
  );

  if (!decision) {
    return undefined;
  }

  if (stage === AdvisorSituationKey.MultiCurrency) {
    const multiCurrency = formatMultiCurrencyExampleForAi(decision.action, t);
    if (multiCurrency) {
      return prefixPortfolioLabel(multiCurrency, options.portfolioLabel);
    }
  }

  const namesById = buildSubAccountNamesById(new Map(), audit);
  const summary = formatAdvisorDecisionAction(
    {
      stage: decision.stage,
      action: decision.action,
      impact: decision.impact ?? OptimizerDecisionImpact.Material,
    },
    namesById,
    audit,
    t,
  );

  if (!summary) {
    return undefined;
  }

  if (decision.stage === OptimizerDecisionStage.TaxLocation) {
    return prefixPortfolioLabel(
      formatTaxLocationExampleForAi(
        audit,
        sanitizeRunSituationExampleForAi(summary),
      ),
      options.portfolioLabel,
    );
  }

  return prefixPortfolioLabel(
    sanitizeRunSituationExampleForAi(summary),
    options.portfolioLabel,
  );
};
